import { instanteDoContato, instantesDeContato, type TipoColisao } from './timelineCinematica'
import type { PosicaoEstabilizacao, AvaliacaoEstabilizacao } from './estabilizacao'

export interface DinamicaSuspensao {
  /** Compressão dinâmica das molas dianteiras em metros (positivo = comprimida/mergulho) */
  compressaoDianteira: number
  /** Compressão dinâmica das molas traseiras em metros (positivo = comprimida) */
  compressaoTraseira: number
  /** Rotação de mergulho (dive) em torno do eixo X em radianos */
  pitchRad: number
  /** Rotação de inclinação em torno do eixo Z em radianos */
  rollRad: number
}

const SEM_DINAMICA: DinamicaSuspensao = { compressaoDianteira: 0, compressaoTraseira: 0, pitchRad: 0, rollRad: 0 }

const somarDinamica = (a: DinamicaSuspensao, b: DinamicaSuspensao): DinamicaSuspensao => ({
  compressaoDianteira: a.compressaoDianteira + b.compressaoDianteira,
  compressaoTraseira: a.compressaoTraseira + b.compressaoTraseira,
  pitchRad: a.pitchRad + b.pitchRad,
  rollRad: a.rollRad + b.rollRad,
})

/**
 * Mergulho (dive) dianteiro num contato frontal: pico de compressão entre
 * `tContato` e `fimFase`, depois oscilação amortecida até t=1.
 *
 * Extraída para servir tanto a colisão frontal isolada quanto a primeira
 * metade do engavetamento (`frontalComTraseira`) — mesma forma, só que com o
 * instante e a janela do PRIMEIRO contato daquele tipo.
 */
function mergulhoDianteiro(
  t: number,
  tContato: number,
  fimFase: number,
  fatorVel: number,
): DinamicaSuspensao {
  if (t < tContato) return SEM_DINAMICA

  if (t <= fimFase) {
    const f = (t - tContato) / Math.max(fimFase - tContato, 1e-6)
    const pico = Math.sin(f * Math.PI)
    return {
      compressaoDianteira: 0.048 * pico * fatorVel,
      compressaoTraseira: -0.032 * pico * fatorVel,
      pitchRad: -0.032 * pico * fatorVel,
      rollRad: 0,
    }
  }

  const tOsc = (t - fimFase) / Math.max(1 - fimFase, 1e-6)
  const amortecimento = Math.exp(-tOsc * 3.8)
  const oscilacao = Math.cos(tOsc * Math.PI * 3.2) * amortecimento
  return {
    compressaoDianteira: 0.024 * oscilacao * fatorVel,
    compressaoTraseira: -0.016 * oscilacao * fatorVel,
    pitchRad: -0.016 * oscilacao * fatorVel,
    rollRad: 0,
  }
}

/**
 * Empinamento (squat) traseiro num contato traseiro: mesma forma de
 * `mergulhoDianteiro`, espelhada — e pela mesma razão, reutilizada na
 * segunda metade do engavetamento.
 */
function empinamentoTraseiro(
  t: number,
  tContato: number,
  fimFase: number,
  fatorVel: number,
): DinamicaSuspensao {
  if (t < tContato) return SEM_DINAMICA

  if (t <= fimFase) {
    const f = (t - tContato) / Math.max(fimFase - tContato, 1e-6)
    const pico = Math.sin(f * Math.PI)
    return {
      compressaoDianteira: -0.028 * pico * fatorVel,
      compressaoTraseira: 0.042 * pico * fatorVel,
      pitchRad: 0.026 * pico * fatorVel,
      rollRad: 0,
    }
  }

  const tOsc = (t - fimFase) / Math.max(1 - fimFase, 1e-6)
  const amortecimento = Math.exp(-tOsc * 3.5)
  const oscilacao = Math.cos(tOsc * Math.PI * 3.0) * amortecimento
  return {
    compressaoDianteira: -0.014 * oscilacao * fatorVel,
    compressaoTraseira: 0.02 * oscilacao * fatorVel,
    pitchRad: 0.014 * oscilacao * fatorVel,
    rollRad: 0,
  }
}

/**
 * Dinâmica de suspensão e amortecimento (massa-mola-amortecedor) para a cinemática.
 *
 * Calcula a transferência de carga entre eixos dianteiro e traseiro durante frenagens
 * e colisões, reproduzindo o mergulho (dive) dianteiro no impacto e oscilação amortecida
 * de retorno no repouso do veículo.
 */
export function calcularDinamicaSuspensao(
  tipo: TipoColisao,
  tempo: number,
  velocidadeKmh = 50,
): DinamicaSuspensao {
  const t = Math.max(0, Math.min(1, tempo))
  const tContato = instanteDoContato(tipo)
  const fatorVel = Math.min(1.6, Math.max(0.4, velocidadeKmh / 50))

  if (tipo === 'frontal') return mergulhoDianteiro(t, tContato, 0.5, fatorVel)

  if (tipo === 'traseira') return empinamentoTraseiro(t, tContato, 0.48, fatorVel)

  if (tipo === 'frontalComTraseira') {
    const tSecundario = instantesDeContato(tipo)[1] ?? tContato
    const doPrimeiro = mergulhoDianteiro(t, tContato, tContato + 0.33, fatorVel)
    const doSecundario = empinamentoTraseiro(t, tSecundario, tSecundario + 0.3, fatorVel)
    return somarDinamica(doPrimeiro, doSecundario)
  }

  if (tipo === 'lateral') {
    if (t < tContato) return SEM_DINAMICA

    const f = Math.min(1, (t - tContato) / 0.35)
    // o impacto chega pelo lado do motorista (+X): espelho da rolagem de
    // carroceria de sempre, com o sinal de `ROLAGEM_SOBRE_O_MOTORISTA_RAD`
    const roll = -Math.sin(f * Math.PI) * 0.035 * fatorVel
    return { compressaoDianteira: 0, compressaoTraseira: 0, pitchRad: 0, rollRad: roll }
  }

  return SEM_DINAMICA
}

/**
 * Avalia se a suspensão do veículo foi anulada pela estabilização (doutrina CBMMG).
 *
 * Princípio do salvamento veicular:
 * Sobre 4 rodas, se a equipe não anula a suspensão preenchendo os vazios entre
 * o chassi e o solo com calços/blocos/cunhas, qualquer movimentação ou uso de
 * ferramentas hidráulicas gera oscilação que é transmitida diretamente à coluna da vítima.
 */
export function avaliarAnulacaoSuspensao(
  posicao: PosicaoEstabilizacao,
  avaliacao: AvaliacaoEstabilizacao,
): { anulada: boolean; motivo: string } {
  if (posicao !== 'quatroRodas') {
    return {
      anulada: true,
      motivo: 'O veículo não está apoiado sobre as rodas; suspensão descarregada.',
    }
  }

  if (avaliacao.primariaConcluida) {
    return {
      anulada: true,
      motivo: 'Suspensão anulada: calços sob o chassi bloqueiam o movimento elástico das molas.',
    }
  }

  return {
    anulada: false,
    motivo: 'Suspensão ativa: veículo suscetível a balanço e vibrações que agravam o trauma raquimedular (TRM).',
  }
}

